import React from "react";
import styled, { css } from "styled-components";
import { NavItem, Nav, Navbar } from "reactstrap";
import { theme } from "../../constants/theme";
import { BANNER_HEIGHT } from "../Banner";

export const NavbarLogo = styled.img`
  width: 34px;
  height: auto;
  margin-right: 0.6rem;
`;

export const NavbarItem = styled(NavItem)`
  width: ${({ width }) => width} !important;
`;

export const NavStyled = styled(Nav)`
  gap: 0.25rem;

  .home-icon {
    display: inline-flex;
    margin-right: 0.35rem;
    opacity: 0.85;
    vertical-align: -3px;
  }

  .nav-item a {
    position: relative;
    font-family: ${theme.font.body};
    font-weight: ${theme.weight.medium};
    font-size: 0.95rem;
    letter-spacing: 0.01em;
    padding: 0.5rem 0.85rem;
    border-radius: ${theme.radius.md};
    transition: color ${theme.motion.base}, background-color ${theme.motion.base}, opacity ${theme.motion.base};

    &:hover {
      opacity: 1 !important;
      background-color: rgba(46, 58, 82, 0.06);
    }

    &.active {
      color: ${theme.color.slate} !important;
      background-color: rgba(245, 168, 138, 0.18);
    }

    i.fa {
      margin-right: 0.35rem;
      opacity: 0.8;
    }
  }
`;

// The Now-UI-Kit base adds `.navbar-transparent` when at top of page and
// removes it once scrolled past 100px. We override both states with our tokens:
// - transparent state: white links on gradient-scrimmed hero
// - scrolled state: warm off-white pill with blur, slate text
export const StyledNavbar = styled(Navbar)`
  font-family: ${theme.font.body};
  padding: 0.65rem 0;
  transition: background-color ${theme.motion.base}, box-shadow ${theme.motion.base}, backdrop-filter ${theme.motion.base};

  .navbar-brand {
    font-family: ${theme.font.display};
    font-weight: ${theme.weight.semibold};
    font-size: 1.15rem;
    letter-spacing: -0.015em;
    display: inline-flex;
    align-items: center;
    margin: 0;
    padding: 0.35rem 0.5rem 0.35rem 0;
    color: ${theme.color.slate};
    transition: color ${theme.motion.base};
    font-variation-settings: "opsz" 96;
  }

  .navbar-toggler-bar {
    background-color: ${theme.color.slate};
  }

  /* Override Paper Kit's 15px link margins and uppercase 12px nav text. */
  .navbar-nav .nav-item .nav-link {
    margin: 0.15rem 0;
    padding: 0.5rem 0.85rem;
    font-family: ${theme.font.body};
    font-size: 0.95rem;
    font-weight: ${theme.weight.medium};
    letter-spacing: 0.01em;
    line-height: 1.5;
    text-transform: none;
    opacity: 1;
  }

  /* Default = scrolled state (solid, over page content) */
  background-color: rgba(248, 244, 239, 0.88);
  backdrop-filter: saturate(140%) blur(10px);
  -webkit-backdrop-filter: saturate(140%) blur(10px);
  box-shadow: 0 1px 0 ${theme.color.border}, ${theme.shadow.sm};

  .nav-item a,
  .navbar-brand {
    color: ${theme.color.slate};
  }

  /* Transparent-at-top state (over the hero) */
  &.navbar-transparent {
    background-color: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    box-shadow: none;

    .navbar-brand {
      color: #FDFBF7;
    }

    .nav-item a {
      color: rgba(253, 251, 247, 0.88);

      &:hover {
        background-color: rgba(253, 251, 247, 0.12);
        color: #FDFBF7;
      }

      &.active {
        color: #FDFBF7 !important;
        background-color: rgba(253, 251, 247, 0.18);
      }
    }

    .navbar-toggler-bar {
      background-color: #FDFBF7;
    }
  }

  @media (max-width: 991.98px) {
    padding: 0.45rem 0;

    /* Paper Kit keeps the collapsed drawer translated off-canvas but leaves
       it visibility-visible, which puts invisible links in the keyboard
       tab order. Visibility participates in the existing drawer transition:
       it becomes visible when opened and hidden after the close animation. */
    .navbar-collapse {
      visibility: hidden;
    }

    .nav-open & .navbar-collapse {
      visibility: visible;
    }

    > .container {
      padding-right: 1rem;
      padding-left: 1rem;
    }

    .navbar-translate {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 3rem;
    }

    .navbar-brand {
      flex: 1 1 auto;
      width: auto !important;
      min-width: 0;
      max-width: calc(100% - 3rem);
      margin: 0;
      padding: 0;
      font-size: 0.9rem;
    }

    .navbar-brand span.navbar-brand-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .navbar-toggler {
      position: static;
      flex: 0 0 2.75rem;
      width: 2.75rem;
      margin: 0;
      padding: 0.45rem 0.6rem;
    }
  }

  ${({ $bannerOpen }) =>
    $bannerOpen &&
    css`
      /* Margin tracks the live banner-stack height published by
         Navbar.js.  Falls back to the historical mobile-tuned
         constant when the var hasn't been set yet (first paint
         before useLayoutEffect runs). */
      margin-top: var(--banner-stack-height, calc(${BANNER_HEIGHT} + 3rem));
    `}
`;

export const StyledNavLink = ({ children, ...props }) => (
  <a className="nav-link" {...props}>
    {children}
  </a>
);
